import * as THREE from 'three';
import { PlanogramRendererResources } from './Resources.js';

/**
 * Transform controller drawn around the current selection: a wireframe transform box,
 * box-shaped corner resize handles, and one rotation ring per axis.
 * X = red, Y = green, Z = blue. Lives on GIZMO_LAYER so it has its own pick pass.
 */
export class PlanogramComponentTransformController extends THREE.Group {

    /**
     * @param {THREE.Box3} box combined bounding box of the selection (world space)
     * @param {"x"|"y"|"z"|null} [visibleAxis]
     */
    constructor( box, visibleAxis = null ) {
        super();
        this.name = "componentTransformController";
        this.layers.set( PlanogramRendererResources.GIZMO_LAYER );

        const size = box.getSize( new THREE.Vector3() );
        const centre = box.getCenter( new THREE.Vector3() );
        // Ring radius from the box diagonal so the rings sit around the selection.
        const radius = 0.3 * Math.max( size.length(), 1 );
        const tube = Math.max( radius * 0.015, 0.25 );
        const handle = Math.max( radius * 0.05, 0.25 );

        // Wireframe transform box around the whole selection (not pickable).
        const boxOutline = new THREE.LineSegments(
            new THREE.EdgesGeometry( new THREE.BoxGeometry(
                Math.max( size.x, 0.01 ), Math.max( size.y, 0.01 ), Math.max( size.z, 0.01 ) ) ),
            new THREE.LineBasicMaterial( { color: 0x000000, depthTest: false, transparent: true } )
        );
        boxOutline.userData.role = "box";
        boxOutline.renderOrder = 10000;
        boxOutline.layers.set( PlanogramRendererResources.GIZMO_LAYER );
        this.add( boxOutline );

        // Box-shaped resize handles on each of the 8 corners.
        const handleGeometry = new THREE.BoxGeometry( handle, handle, handle );
        for ( const sx of [ -1, 1 ] ) {
            for ( const sy of [ -1, 1 ] ) {
                for ( const sz of [ -1, 1 ] ) {
                    const corner = new THREE.Mesh(
                        handleGeometry,
                        new THREE.MeshBasicMaterial( { color: 0x000000, depthTest: false, transparent: true } )
                    );
                    corner.position.set( sx * size.x / 2, sy * size.y / 2, sz * size.z / 2 );
                    corner.userData.role = "resize";
                    corner.userData.corner = new THREE.Vector3( sx, sy, sz );
                    corner.renderOrder = 10001;
                    corner.layers.set( PlanogramRendererResources.GIZMO_LAYER );
                    this.add( corner );
                }
            }
        }

        // A torus lies in its local XY plane, so its rotation axis is local +Z.
        const rings = [
            { axis: "x", colour: 0xff0000, euler: new THREE.Euler( 0, Math.PI / 2, 0 ) },
            { axis: "y", colour: 0x00ff00, euler: new THREE.Euler( Math.PI / 2, 0, 0 ) },
            { axis: "z", colour: 0x0000ff, euler: new THREE.Euler( 0, 0, 0 ) }
        ];

        for ( const { axis, colour, euler } of rings ) {
            const ring = new THREE.Mesh(
                new THREE.TorusGeometry( radius, tube, 8, 64 ),
                new THREE.MeshBasicMaterial( { color: colour, depthTest: false, transparent: true } )
            );
            ring.setRotationFromEuler( euler );
            ring.userData.axis = axis;
            ring.userData.role = "rotate";
            ring.renderOrder = 10000;
            ring.layers.set( PlanogramRendererResources.GIZMO_LAYER );
            ring.visible = visibleAxis === null || axis === visibleAxis;
            this.add( ring );
        }

        this.position.copy( centre );
    }

    /**
     * World-space unit vector of the given ring's rotation axis.
     * @param {"x"|"y"|"z"} axis
     * @returns {THREE.Vector3}
     */
    axisVector( axis ) {
        return axis === "x" ? new THREE.Vector3( 1, 0, 0 ) :
            axis === "y" ? new THREE.Vector3( 0, 1, 0 ) :
            new THREE.Vector3( 0, 0, 1 );
    }

    dispose() {
        this.children.forEach( child => {
            child.geometry?.dispose();
            child.material?.dispose();
        } );
        this.removeFromParent();
    }
}
